import { LoadingDot } from '@/components/LoadingDot/LoadingDot'
import SelectModelCompare, {
  SelectTranslateModel,
} from '@/components/SelectModelCompare/SelectModelCompare'
import { SvgIcon } from '@/components/SvgIcon/SvgIcon'
import { toastController } from '@/controllers/toastController'
import { commonUtils } from '@/helpers/commonUtils'
import { storageHelper } from '@/helpers/storageHelper'
import { translateService } from '@/services/translateService'
import { userService } from '@/services/userService'
import { RetweetOutlined } from '@ant-design/icons'
import { Popover, Spin, Tooltip } from 'antd'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Draggable from 'react-draggable'
import style from './CompareTranslateDialog.module.less'

type CompareTranslateDialogProps = {
  text: string
  rect: DOMRect
  useType: PostDataTypes.UseType
  replaceTagHtmlList: string[]
  translateEl: HTMLElement
  onClose?: () => void
  currentModel: ServerDataTypes.Model
  sourceLang: string
  targetLang: string
}

export type CompareData = {
  loading: boolean
  result: string
  model: ServerDataTypes.Model
  hasToken: boolean
  errMsg?: string
}

export const CompareTranslateDialog = observer((props: CompareTranslateDialogProps) => {
  const {
    text,
    rect,
    useType,
    replaceTagHtmlList,
    onClose,
    currentModel,
    sourceLang,
    targetLang,
    translateEl,
  } = props
  const ref = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null)
  const [dialogWidth, setDialogWidth] = useState(0)
  // 添加初始滚动位置记录
  const initialScrollRef = useRef<{ x: number; y: number } | null>(null)
  // 添加拖动状态跟踪
  const [isDragging, setIsDragging] = useState(false)
  const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [compareData, setCompareData] = useState<CompareData[]>([])
  const [modelLoading, setModelLoading] = useState(false)

  const replaceTagHtmlListMemo = useMemo(() => {
    const parser = new DOMParser()
    return replaceTagHtmlList.map((htmlStr, idx) => {
      const doc = parser.parseFromString(htmlStr, 'text/html')
      return doc.body.outerHTML
    })
  }, [replaceTagHtmlList])

  // 常量定义
  const TOKEN_INSUFFICIENT_ERROR =
    '模型额度已用完，无法继续查看多模型翻译对比，升级后立即继续对比，快去升级吧！'

  // 提取创建 CompareData 项的逻辑
  const createCompareDataItem = (model: ServerDataTypes.Model): CompareData => {
    // 免费模型
    if (model.level === 1) {
      return {
        loading: true,
        result: '',
        model,
        hasToken: true,
      }
    }

    // 收费模型,检查token是否足够
    const hasToken = userService.checkTokenCountByModel(model) > 0
    if (hasToken) {
      return {
        loading: true,
        result: '',
        model,
        hasToken: true,
      }
    }

    return {
      loading: false,
      result: '',
      model,
      hasToken: false,
      errMsg: TOKEN_INSUFFICIENT_ERROR,
    }
  }

  // 提取更新翻译结果的回调函数
  const updateTranslateResult = (itemResult: any) => {
    setCompareData((prev) => {
      const newData = [...prev]
      const targetIndex = newData.findIndex((item) => item.model.key === itemResult.model.key)
      if (targetIndex !== -1) {
        let result = itemResult.result
        replaceTagHtmlListMemo.forEach((str, idx) => {
          result = result.replace(`<b${idx}></b${idx}>`, str)
        })

        newData[targetIndex] = {
          ...newData[targetIndex],
          loading: false,
          result,
          errMsg: itemResult.errMsg,
        }
      }
      return newData
    })
  }

  // 提取执行翻译的逻辑
  const performTranslate = async (models: ServerDataTypes.Model[]) => {
    const textList = models.map((model) => ({
      text,
      model,
      fromLang: sourceLang,
      toLang: targetLang,
    }))

    await translateService.compareTranslate(textList, updateTranslateResult, 'pdf_compare')
  }

  // 添加单个模型到对比列表
  const addModelToCompare = async (model: SelectTranslateModel) => {
    const newModelData = createCompareDataItem(model)
    const newCompareData = [newModelData, ...compareData]
    setCompareData(newCompareData)

    // 只有有token的模型才进行翻译
    if (newModelData.hasToken) {
      await performTranslate([model])
    }
  }

  // 从对比列表中移除模型
  const removeModelFromCompare = (modelKey: string) => {
    const newCompareData = compareData.filter((item) => item.model.key !== modelKey)
    setCompareData(newCompareData)
  }

  // 计算初始位置
  useLayoutEffect(() => {
    const triggerRect = rect
    const triggerBottom = triggerRect.bottom
    const triggerTop = triggerRect.top
    const triggerLeft = triggerRect.left
    const triggerRight = triggerRect.right
    const triggerWidth = triggerRect.width
    const triggerCenterX = triggerLeft + triggerWidth / 2

    const maxWidth = 600
    const minWidth = 230

    // 临时创建一个元素来测量尺寸
    const tempElement = document.createElement('div')
    tempElement.style.position = 'fixed'
    tempElement.style.visibility = 'hidden'
    tempElement.style.minWidth = `${minWidth}px`
    tempElement.style.maxWidth = `${maxWidth}px`
    tempElement.style.width = `${Math.max(minWidth, Math.min(maxWidth, triggerWidth))}px`
    tempElement.style.height = 'auto'
    tempElement.className = ref.current?.className || ''
    document.body.appendChild(tempElement)

    // const dialogWidth = Math.max(200, Math.min(300, triggerWidth))

    let dialogWidth = 0
    // 根据触发元素宽度动态调整弹窗宽度
    if (triggerWidth > maxWidth) {
      dialogWidth = maxWidth
      setDialogWidth(maxWidth)
    } else if (triggerWidth < minWidth) {
      dialogWidth = minWidth
      setDialogWidth(minWidth)
    } else {
      dialogWidth = triggerWidth
      setDialogWidth(triggerWidth)
    }

    const dialogHeight = 400 // 预估高度

    document.body.removeChild(tempElement)

    // 获取视口尺寸
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let dialogLeft: number
    let dialogTop: number

    // 水平定位逻辑 - 始终居中对齐
    dialogLeft = triggerCenterX - dialogWidth / 2

    // 水平边界处理
    const minLeft = 8
    const maxLeft = viewportWidth - dialogWidth - 8

    if (dialogLeft < minLeft) {
      dialogLeft = minLeft
    } else if (dialogLeft > maxLeft) {
      dialogLeft = maxLeft
    }

    // 垂直定位逻辑
    const spaceBelow = viewportHeight - triggerBottom
    const spaceAbove = triggerTop
    const minSpaceNeeded = dialogHeight + 8

    if (spaceBelow >= minSpaceNeeded) {
      dialogTop = triggerBottom + 8
    } else if (spaceAbove >= minSpaceNeeded) {
      dialogTop = triggerTop - dialogHeight - 8
    } else {
      if (spaceBelow > spaceAbove) {
        dialogTop = triggerBottom + 8
      } else {
        dialogTop = triggerTop - dialogHeight - 8
      }
    }

    // 垂直边界处理
    const minTop = 8
    const maxTop = viewportHeight - dialogHeight - 8

    if (dialogTop < minTop) {
      dialogTop = minTop
    } else if (dialogTop > maxTop) {
      dialogTop = maxTop
    }

    // 设置初始位置
    setPosition({ x: dialogLeft, y: dialogTop })
  }, [])

  // 监听滚动
  useEffect(() => {
    const handleScroll = (event: Event) => {
      if (!initialScrollRef.current) return

      // 计算滚动偏移量
      const currentScrollX = window.scrollX
      const currentScrollY = window.scrollY
      const deltaX = currentScrollX - initialScrollRef.current.x
      const deltaY = currentScrollY - initialScrollRef.current.y

      // 更新面板位置
      setPosition((prev) => {
        if (!prev) return null
        return {
          x: prev.x + deltaX,
          y: prev.y - deltaY,
        }
      })

      // 更新滚动基准位置
      initialScrollRef.current = {
        x: currentScrollX,
        y: currentScrollY,
      }
    }

    // 记录初始滚动位置
    if (!initialScrollRef.current) {
      initialScrollRef.current = {
        x: window.scrollX,
        y: window.scrollY,
      }
    }

    document.addEventListener('scroll', handleScroll, true) // 使用捕获模式确保能监听到所有滚动事件
    return () => {
      document.removeEventListener('scroll', handleScroll, true)
    }
  }, [])

  // 监听点击外部
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDragging) {
        return
      }

      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose?.()
      }
    }
    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [isDragging])

  // 初始化数据
  useEffect(() => {
    const initData = async () => {
      setModelLoading(true)
      let dataList = await userService.getLLMModelList('translate')
      // 非Ultra会员，过滤掉顶级模型
      if (userService.vipVersion !== 'ultra') {
        dataList = dataList.filter((item) => item.level !== 3)
      }
      // const currentModel = userService.pdfProTranslateModel
      // 先过滤出非当前模型
      // const compareModelList = dataList.filter((item) => item.key !== currentModel.key)
      // 再过滤出已经在配置页面中去掉的模型
      const excludeModelList = userService.webTranslateCompareModelExcludeModelList
      const excludeModelListSet = new Set(excludeModelList)
      const filteredModelList = dataList
        .filter((item) => !excludeModelListSet.has(item.key))
        .sort((a, b) => b.level - a.level)

      // 使用提取的函数创建对比数据
      const compareData: CompareData[] = filteredModelList.map(createCompareDataItem)

      setCompareData(compareData)
      setModelLoading(false)

      // 获取有token的模型进行翻译
      const modelsWithToken = compareData.filter((item) => item.hasToken).map((item) => item.model)

      if (modelsWithToken.length > 0) {
        await performTranslate(modelsWithToken)
      }
    }

    initData()

    return () => {
      // 清理拖动超时
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current)
      }
      translateService.stopCompareTranslate()
    }
  }, [])

  const onModelCompareChange = async (model: SelectTranslateModel) => {
    if (model.isSelected) {
      await addModelToCompare(model)
    } else {
      removeModelFromCompare(model.key)
    }
  }

  // 位置未计算完成时不渲染
  if (!position) {
    return null
  }

  return (
    <Draggable
      position={position}
      handle='.drag-handle'
      onStart={() => {
        // 拖动开始时设置拖动状态
        setIsDragging(true)
        // 清除之前的超时
        if (dragTimeoutRef.current) {
          clearTimeout(dragTimeoutRef.current)
        }
      }}
      onDrag={(e, data) => {
        // 在拖动时更新位置状态
        setPosition({ x: data.x, y: data.y })
      }}
      onStop={() => {
        // 拖动结束后延迟一段时间再允许点击外部关闭
        // 这样可以避免拖动结束时的mouseup事件被误认为是点击外部
        dragTimeoutRef.current = setTimeout(() => {
          setIsDragging(false)
        }, 100) // 100ms延迟，足够避免拖动结束时的点击事件冲突
      }}
    >
      <div
        className={classNames(style.compareDialog)}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        style={{ width: `${dialogWidth}px` }}
      >
        <div className={classNames(style.header, 'drag-handle')} style={{ cursor: 'move' }}>
          <div className={classNames(style.title)}>多模型翻译对比</div>

          <Popover
            arrow={false}
            getPopupContainer={(el) => el.parentElement!}
            placement='bottomLeft'
            align={{
              offset: [-20, 0],
            }}
            content={
              <SelectModelCompare
                className={classNames(style.selectModelCompare)}
                excludeModelList={userService.webTranslateCompareModelExcludeModelList}
                onHandleModelChange={(model) => {
                  onModelCompareChange(model)

                  const excludeModelList =
                    storageHelper.get(['webTranslateCompareModelExcludeModelList'])
                      .webTranslateCompareModelExcludeModelList ?? []

                  if (model.isSelected) {
                    if (excludeModelList.includes(model.key)) {
                      excludeModelList.splice(excludeModelList.indexOf(model.key), 1)
                    }
                  } else {
                    if (!excludeModelList.includes(model.key)) {
                      excludeModelList.push(model.key)
                    }
                  }
                  storageHelper.set({
                    webTranslateCompareModelExcludeModelList: excludeModelList,
                  })

                  userService.webTranslateCompareModelExcludeModelList = excludeModelList
                }}
              />
            }
            trigger={'click'}
            overlayInnerStyle={{
              padding: 0,
              background: '#FFFFFF',
              boxShadow: '0px 2px 16px 0px rgba(93,93,93,0.15)',
              borderRadius: '10px',
            }}
            {...props}
          >
            <div className={classNames(style.model)}>设置</div>
          </Popover>

          <div className={classNames(style.close)} onClick={() => onClose?.()}>
            <SvgIcon fontClass='icon-line-global_close' style={{ width: '100%', height: '100%' }} />
          </div>
        </div>

        {modelLoading && (
          <Spin spinning={modelLoading} className={classNames(style.loading)} size='small' />
        )}

        <div className={classNames(style.contentList)}>
          {compareData.map((item) => (
            <div className={classNames(style.contentItem)} key={item.model.key}>
              <div className={classNames(style.itemHeader)}>
                <img src={item.model.icon} className={classNames(style.icon)} />
                <div className={classNames(style.name)} title={item.model.name}>
                  {item.model.name}
                </div>
                {item.model.level === 2 && (
                  <div className={classNames(style.proBadge, style.max)}>Max</div>
                )}
                {item.model.level === 3 && (
                  <div className={classNames(style.proBadge, style.ultra)}>Ultra</div>
                )}
              </div>

              <div className={classNames(style.result)}>
                {item.loading ? (
                  <LoadingDot />
                ) : (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: commonUtils.cleanHtml(item.result) || item.errMsg || '',
                    }}
                  ></span>
                )}
              </div>

              {!item.hasToken && (
                <div className={classNames(style.upgradeBox)}>
                  <div
                    className={classNames(style.btn)}
                    onClick={() => {
                      userService.doPay({
                        from: 'pdf_compare',
                        feature: 'pdfPro',
                      })
                      setTimeout(() => {
                        onClose?.()
                      }, 200)
                    }}
                  >
                    去升级
                  </div>
                </div>
              )}

              {item.result && !item.loading && !item.errMsg && (
                <Tooltip title='替换' getPopupContainer={(e) => ref.current!} placement='top'>
                  <div
                    className={classNames(style.copy)}
                    style={{ right: '40px' }}
                    onClick={() => {
                      translateEl.innerHTML = commonUtils.cleanHtml(item.result) || ''
                    }}
                  >
                    <RetweetOutlined style={{ fontSize: '14px', color: '#000' }} />
                  </div>
                </Tooltip>
              )}

              {item.result && !item.loading && !item.errMsg && (
                <Tooltip title='复制' getPopupContainer={(e) => ref.current!} placement='top'>
                  <div
                    className={classNames(style.copy)}
                    onClick={() => {
                      const tempDiv = document.createElement('div')
                      tempDiv.innerHTML = commonUtils.cleanHtml(item.result) || ''
                      tempDiv.querySelectorAll('.katex-mathml').forEach((el) => {
                        el.remove()
                      })
                      const textContent = tempDiv.textContent || tempDiv.innerText || ''

                      commonUtils.copyToClipboard(textContent)
                      toastController.success('已复制')
                    }}
                  >
                    <SvgIcon
                      fontClass='icon-line-global_Copy'
                      style={{
                        width: '14px',
                        height: '14px',
                      }}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      </div>
    </Draggable>
  )
})
